/** Public legal pages for Razorpay / compliance (Development Express). */

export const LEGAL_CONTACT = {
  company: 'Development Express',
  platform: 'Development Express',
  email: 'contact@developmentexpress.in',
  phone: '+91-8408000084',
  whatsapp: 'https://wa.me/918408000084',
  address: 'Karad, Satara District, Maharashtra, India — 415110',
  website: 'https://app.developmentexpress.in',
  marketing: 'https://developmentexpress.in',
  gstNote: 'GST-compliant tax invoices under CGST Act s.31 / Rule 46 (18% GST; CGST+SGST or IGST by place of supply). ECO TCS under s.52 where applicable.',
};

export const POLICIES = {
  terms: {
    title: 'Terms & Conditions',
    sections: [
      {
        heading: '1. Service',
        body:
          'Development Express is a digital platform for booking construction machinery. Contractors (clients) book equipment from registered machine owners. All commercial terms, rates, and availability are shown in the app before confirmation.',
      },
      {
        heading: '2. Account & use',
        body:
          'You must register with accurate business and contact details. Direct cash payments to operators on site are prohibited where wallet-only policy applies. Misuse, fraud, or bypassing the platform may lead to account suspension.',
      },
      {
        heading: '3. Payment policy',
        body:
          'Booking advance and wallet recharge are processed through Razorpay (UPI, cards, net banking, and other enabled methods). Amounts are in Indian Rupees (INR). GST at 18% applies on taxable supplies as shown at checkout.\n\nStandard (spot/hourly/daily/weekly/monthly) bookings: a mandatory wallet advance of 50% of the grand total (including GST) is debited at confirmation.\n\nCustom/Contract bookings (monthly/quarterly and longer): a Booking Advance of 15% of the Total Hire Value is charged, plus 100% of any client-payable Mobilization Charge, collected only after the quotation is approved by the owner and platform.\n\nFor Standard bookings, the remaining value is settled per platform completion and ledger rules. For Custom/Contract bookings, the remaining hire value is billed per cycle through Running Account (RA) bills, each with a 15-day review window before payment is due.',
      },
      {
        heading: '4. Custom/Contract Project Billing',
        body:
          'For Custom/Contract project bookings, the agreed hire value is billed through monthly or quarterly Running Account (RA) bills over the contract period, based on the guaranteed-hours terms agreed at booking. Each RA bill has a 15-day review period during which the client may raise an itemised, evidence-based dispute; undisputed amounts remain payable. Bills not disputed within the review window are deemed accepted.',
      },
      {
        heading: '5. Wallet credit policy',
        body:
          'Eligible refunds, cancellations, and corrections are credited to the Development Express wallet (wallet credit), not cash, unless a Razorpay instrument refund is required by law or payment-provider rules. Wallet credits may be used for future bookings and recharges on the platform.',
      },
      {
        heading: '6. Cancellation policy',
        body:
          'Client cancellations before machine dispatch attract penalties as defined in our Cancellation Policy. See Cancellation Policy at /cancellation-policy for full terms.',
      },
      {
        heading: '7. Fraud & blacklist policy',
        body:
          'Fraudulent registrations, fake documents, payment abuse, chargebacks in bad faith, wallet misuse, or attempts to bypass Development Express may result in immediate suspension and placement on an internal fraud/blacklist. Blacklisted clients may be blocked from new bookings. Owners may be informed of relevant fraud signals as needed to protect the marketplace. Development Express may share necessary details with payment partners and authorities where legally required.',
      },
      {
        heading: '8. Bookings',
        body:
          'Standard bookings are confirmed when the client pays the required advance and the booking is accepted in the system.\n\nCustom/Contract bookings go through a quotation process first (owner review, then automated platform compliance checks) before the client is asked to digitally accept final terms and pay the advance. The booking is confirmed only after this full process completes.',
      },
      {
        heading: '9. Liability',
        body:
          'Development Express facilitates booking and payments between parties. Operational safety, site access, and machine condition remain the responsibility of the owner and client as per their agreement and applicable law.',
      },
      {
        heading: '10. Governing law & jurisdiction',
        body:
          'These Terms are governed by the laws of India. Any dispute, claim or controversy arising out of or relating to these Terms or use of the Development Express platform shall be resolved as follows: (1) Conciliation first — parties shall attempt resolution through conciliation under Part III of the Arbitration and Conciliation Act, 1996 within 90 days of written notice of dispute. (2) Arbitration — if conciliation fails, the dispute shall be referred to a sole arbitrator mutually appointed under Part I of the Arbitration and Conciliation Act, 1996. Seat and venue of arbitration: Karad, Satara, Maharashtra, India. Language: English. Award shall be final and binding. (3) Courts — courts at Karad, Satara shall have jurisdiction only for interim relief under Section 9 or enforcement under Section 36 of the Arbitration and Conciliation Act, 1996. MSME registered users may additionally approach the MSME Facilitation Council under MSMED Act 2006 Section 18.',
      },
      {
        heading: '11. Grievance Redressal (IT Intermediary Guidelines Rules 2021 & MSMED Amendment 2026)',
        body:
          'Grievance Officer: To be notified upon incorporation · Development Express · Email: contact@developmentexpress.in. All grievances acknowledged within 24 hours and resolved within 7 days per IT Intermediary Rules 2021. For MSMED disputes, ODR, arbitral awards and full escalation details, see our Grievance Redressal page at /grievance.',
      },
    ],
  },
  privacy: {
    title: 'Privacy Policy',
    sections: [
      {
        heading: '1. Data we collect',
        body:
          'We collect name, company name, phone, email, booking and payment records, wallet transactions, and technical logs needed to run the service securely.',
      },
      {
        heading: '2. How we use data',
        body:
          'Data is used to authenticate users, process payments, send booking and payment notifications (email/SMS/WhatsApp where enabled), prevent fraud, and comply with law.',
      },
      {
        heading: '3. Payment data',
        body:
          'Card and bank payments are processed by Razorpay. We do not store full card numbers. We store payment references, order IDs, and wallet ledger entries.',
      },
      {
        heading: '4. Sharing',
        body:
          'We share data with service providers (hosting, Supabase database, Razorpay, messaging providers) only as needed to operate Development Express. We do not sell personal data.',
      },
      {
        heading: '5. Retention & security',
        body:
          'Records are retained as required for accounting, tax, and dispute resolution. We use HTTPS, access controls, and industry-standard practices to protect data.',
      },
      {
        heading: '6. Deleting your account',
        body:
          'You may delete your Development Express account from the mobile app (Profile → Delete my account) or on the web at https://app.developmentexpress.in/delete-account. Both flows require a fresh SMS OTP to your registered mobile and typing DELETE to confirm.\n\nWhat is deleted or anonymized: sign-in access for that account, phone number, bank details, profile photo, and uploaded KYC document files (and related KYC rows). Your display name and login email are replaced with anonymized placeholders.\n\nWhat is retained: booking, wallet ledger, settlement, invoice, and GST records needed for accounting and disputes; PAN, GSTIN, and company/legal name where required for Income Tax TDS and GST (retention required by law under DPDP).\n\nDeletion is blocked while you have wallet balance greater than zero, pending withdrawals or top-ups, pending settlement transfers, active or disputed bookings, or unpaid RA bills. Field Experts (operators) cannot delete while on an in-flight booking. Open support grievances are a warning only and do not block deletion.',
      },
      {
        heading: '7. Data retention',
        body:
          'Financial, invoice, GST, TDS, booking and wallet ledger records are retained for as long as required under applicable Indian law (including the Income-tax Act, 1961 and the CGST Act, 2017) — currently up to 8 years — and then deleted.\n\nPAN, GSTIN and legal/business name are retained with those records for the same legal purpose.\n\nKYC documents (PAN/Aadhaar/GST certificate images), profile photo, bank details and saved addresses are deleted when the account is deleted, unless required for an ongoing dispute or legal proceeding.',
      },
      {
        heading: '8. Your rights & contact',
        body:
          'You may request correction of your personal data, or account deletion where applicable, by using https://app.developmentexpress.in/delete-account, the in-app Profile → Delete my account flow, emailing contact@developmentexpress.in, or WhatsApp +91-8408000084. Indian law (including DPDP) applies.',
      },
    ],
  },
  refund: {
    title: 'Refund Policy',
    sections: [
      {
        heading: '1. Wallet recharge',
        body:
          'Wallet top-ups via Razorpay are credited to your Development Express wallet after successful payment verification. Failed or duplicate payments are reversed or adjusted in the wallet ledger after investigation.',
      },
      {
        heading: '2. Booking advance',
        body:
          'Booking advance debited from the wallet is held against the confirmed booking. If a booking is cancelled per our Cancellation Policy, eligible amounts are credited back to the wallet (not cash) unless otherwise stated at cancellation.',
      },
      {
        heading: '3. Bank transfer (NEFT/IMPS)',
        body:
          'Manual bank transfers require admin verification. Refunds or corrections for wrong UTR/amount are processed after review and credited to the wallet or reversed per banking rules.',
      },
      {
        heading: '4. Settlement to owners',
        body:
          'Owner payouts are processed per platform settlement rules after work completion and admin/treasury processes. Client wallet refunds and owner settlements are separate flows.',
      },
      {
        heading: '5. Razorpay refunds',
        body:
          'Where a Razorpay payment must be refunded to the original instrument, we initiate refund per Razorpay and bank timelines (typically 5–10 business days).',
      },
    ],
  },
  cancellation: {
    title: 'Cancellation Policy',
    sections: [
      {
        heading: '1. Contract/Custom quotations before payment',
        body:
          'Contract/Custom project quotations may be withdrawn by the client at any point before advance payment is collected, at no charge, since no funds have moved at that stage. The penalty terms below apply only after a booking is confirmed and advance payment has been made.',
      },
      {
        heading: '2. Client cancellation',
        body:
          'Clients may request cancellation of an active booking through Development Express support or in-app flows where available. Cancellation terms depend on dispatch status and time before scheduled work.',
      },
      {
        heading: '3. Penalty',
        body:
          'If a machine is already dispatched or work has started, a penalty may apply (for example, charges equivalent to one hour or as shown in the app at booking time). The remaining advance balance, if any, is credited to the client wallet.',
      },
      {
        heading: '4. Owner / force majeure',
        body:
          'If the owner cannot supply the machine due to breakdown or force majeure, Development Express will work to reschedule or credit the wallet per case review.',
      },
      {
        heading: '5. No-show',
        body:
          'Client no-show after dispatch may result in forfeiture of part of the advance as per the rate card and booking type confirmed in the app.',
      },
    ],
  },
  shipping: {
    title: 'Service Delivery Policy',
    sections: [
      {
        heading: '1. Nature of service',
        body:
          'Development Express provides construction machinery rental services through its online booking platform. There is no physical product shipping. “Delivery” means dispatch of the booked machine and operator (where included) to the client’s site address stated in the booking.',
      },
      {
        heading: '2. Dispatch & timing',
        body:
          'For Standard bookings: after wallet advance payment and owner approval, the machine is scheduled for dispatch. For Custom/Contract bookings: after the client quotation request, owner approval, automated platform compliance checks, and the client digital acceptance and advance payment, the machine is scheduled for dispatch (mobilization). Estimated timing is communicated in the app and via notifications. Delays due to weather, road access, or force majeure are communicated to the client.',
      },
      {
        heading: '3. Site requirements',
        body:
          'The client must provide safe site access, working area, and necessary permissions. Fuel and operator inclusion follow the rate card shown at booking (typically fuel and operator included unless stated otherwise).',
      },
      {
        heading: '4. Completion',
        body:
          'Work is marked complete by the owner in the platform after service on site. The client receives GST invoice (where applicable) after completion status. Settlement between parties follows platform rules.',
      },
    ],
  },
  contact: {
    title: 'Contact Us',
    sections: [
      {
        heading: 'Development Express',
        body:
          'Development Express is the online booking and wallet platform for construction machinery rental (est. 2011, Maharashtra).',
      },
      {
        heading: 'Customer support',
        body:
          'Email: contact@developmentexpress.in\nPhone / WhatsApp: +91-8408000084\nHours: Monday–Saturday, 9:00 AM – 7:00 PM IST',
      },
      {
        heading: 'Registered office / correspondence',
        body: 'Karad, Satara District, Maharashtra, India — PIN 415110',
      },
      {
        heading: 'Website & app',
        body:
          'Marketing: https://developmentexpress.in\nClient & owner app: https://app.developmentexpress.in',
      },
      {
        heading: 'Payments & refunds',
        body:
          'Payment disputes and refund requests: email contact@developmentexpress.in with Razorpay payment ID or booking reference. See Refund Policy and Cancellation Policy on this site.',
      },
    ],
  },
  pricing: {
    title: 'Pricing',
    sections: [
      {
        heading: '1. How pricing works',
        body:
          'Rates are shown per machine in the Development Express app across Standard modes (hourly, daily, weekly, monthly) and Custom/Contract Project mode. Custom/Contract Project bookings are billed per monthly or quarterly cycle with a Guaranteed Hours commitment. Prices include GST display at checkout (18% GST). Fuel and operator are included on rate cards unless noted otherwise.',
      },
      {
        heading: '2. Booking advance',
        body:
          'Standard bookings: clients pay a mandatory wallet advance of 50% of the grand total (including GST) at booking confirmation.\n\nCustom/Contract bookings: a Booking Advance of 15% of the Total Hire Value is charged, plus 100% of any client-payable Mobilization Charge, collected only after quotation approval.\n\nFor Standard bookings, remaining value is settled per platform completion and ledger rules. For Custom/Contract bookings, remaining hire value is billed per cycle through Running Account (RA) bills.',
      },
      {
        heading: '3. Wallet recharge',
        body:
          'Minimum recharge for UPI/card is ₹1 for testing; NEFT/IMPS bank transfer minimum ₹5,000. Payments processed via Razorpay.',
      },
      {
        heading: '4. Quotes',
        body:
          'Custom/Contract projects are requested directly in-app through the Contract Booking quotation flow: client submits terms, owner reviews and accepts, the platform runs automated compliance checks, the client digitally accepts final terms, and then advance payment is collected. For questions, contact contact@developmentexpress.in or WhatsApp +91-8408000084.',
      },
    ],
  },
  grievance: {
    title: 'Grievance Redressal',
    slug: 'grievance',
    sections: [
      {
        heading: 'Grievance Officer',
        body:
          'Designation: Grievance Officer, Development Express. Name: To be notified upon incorporation. Development Express Pvt Ltd · Email: contact@developmentexpress.in · Phone/WhatsApp: +91-8408000084 · Address: Karad, Satara District, Maharashtra 415110. Pre-incorporation note: Until incorporation is complete, the contact details above apply; full named officer particulars will be published on this page upon incorporation.',
      },
      {
        heading: 'Response Timeline',
        body:
          'All grievances acknowledged within 24 hours. Resolved within 7 days as per IT (Intermediary Guidelines and Digital Media Ethics Code) Rules, 2021 [updated 10.02.2026].',
      },
      {
        heading: 'Intermediary Disclosure',
        body:
          'Development Express operates as an Intermediary under the Information Technology Act, 2000 and the IT Intermediary Guidelines Rules, 2021. Users may raise grievances related to platform operations, bookings, payments, and data handling.',
      },
      {
        heading: 'MSMED Act 2006 & Amendment 2026',
        body:
          'Development Express recognises the Micro, Small and Medium Enterprises Development Act, 2006 and the MSMED Amendment 2026. MSME users registered on the permanent Udyam portal may seek remedies for delayed payments and commercial disputes in accordance with applicable law, including mandatory online dispute resolution (ODR) where applicable.',
      },
      {
        heading: 'Online Dispute Resolution (ODR)',
        body:
          'Under the MSMED Amendment 2026, ODR is mandatory for eligible MSME disputes. File and track disputes via the Government of India MSME Samadhaan portal: https://samadhaan.msme.gov.in',
      },
      {
        heading: 'Arbitral Awards (MSMED 2026)',
        body:
          'Where a dispute proceeds to arbitration under applicable law, courts must order payment of a minimum of 50% of the arbitral award amount upfront to the MSME party, as provided under the MSMED Amendment 2026.',
      },
      {
        heading: 'Escalation',
        body:
          'If a grievance is not resolved within 7 days through the Grievance Officer, users may escalate to the MSME Facilitation Council and other remedies under the MSMED Act 2006 and Amendment 2026.',
      },
    ],
  },
};

export const POLICY_PATHS = {
  terms: '/terms',
  privacy: '/privacy',
  deleteAccount: '/delete-account',
  refund: '/refund-policy',
  cancellation: '/cancellation-policy',
  shipping: '/service-delivery-policy',
  contact: '/contact',
  pricing: '/pricing',
  grievance: '/grievance',
};

/** Razorpay “Cancellation and Refunds” — single URL combining both policies. */
export const CANCELLATION_AND_REFUNDS_PATH = '/cancellation-and-refunds';
